import { useCallback, useEffect, useState } from 'react';
import {
  formatCourse,
  matchesCollectionQuery,
  type CollectionWithQuery,
  type ImportDraftItem,
  type RangementPropose,
} from '@mlc/shared';
import { garderImportDraft, jeterImportDraft, listCollections, listImportDrafts, majPropositions } from './api';
import { Link, paths } from './router';
import {
  Banner,
  Button,
  Card,
  CollapsiblePanel,
  DISABLED_SHAPE,
  EmptyState,
  Icon,
  Loading,
  PageHeader,
  badgeClasses,
  buttonClasses,
} from './ui';

/**
 * Le sas (US-71) — « je mets de côté maintenant, je valide plus tard, en une
 * tape ».
 *
 * ### Ce que cet écran doit être, et ce qu'il ne doit surtout pas devenir
 *
 * Quand on tombe sur une recette, on est en train de faire autre chose. Le sas
 * existe pour que la capture ne coûte rien sur le moment ; **il ne sert à rien
 * si la validation coûte cher plus tard**. D'où une carte par entrée, trois
 * actions, et un ordre de rang qui ne se discute pas :
 *
 * | rang | action | ce qu'elle promet |
 * | ---- | ------ | ----------------- |
 * | primaire | **Garder** | une tape, la recette entre, la photo suit |
 * | secondaire | Ouvrir pour corriger | l'écran de validation habituel |
 * | tertiaire | Jeter | l'entrée part, sa photo avec elle |
 *
 * « Garder » **n'ouvre pas de formulaire**, et c'est tout l'objet de la story :
 * le regard humain qu'exige le §4.4 étape 4 a bien lieu — il a lieu ici, sur la
 * carte — il ne coûte simplement plus trente secondes. Les ingrédients sont
 * repliés parce qu'ils servent à *vérifier*, pas à décider : la photo et le
 * titre suffisent à reconnaître ce qu'on avait mis de côté.
 *
 * Le risque, écrit au §6 du plan de vague, est que le sas devienne un
 * cimetière. Ses trois parades sont ici ou pas loin : « Tout garder » pour le
 * vidage du dimanche soir, la pastille pour qu'on sache qu'il y a quelque
 * chose, et la péremption à trente jours côté serveur.
 */

/** Ce qui reste avant la péremption, en jours pleins. */
function joursRestants(expiresAt: string, maintenant: number): number {
  return Math.ceil((new Date(expiresAt).getTime() - maintenant) / (24 * 60 * 60 * 1000));
}

/** Les premières lignes d'ingrédients, pour le repli. */
function lignes(texte: string): string[] {
  return texte.split('\n').map((ligne) => ligne.trim()).filter((ligne) => ligne.length > 0);
}

/**
 * La vignette d'une entrée (US-71, élargie par US-72).
 *
 * Deux sources, et la priorité n'est pas discutable : **la photo jointe
 * d'abord** — elle est sur notre disque, servie par une route qui applique la
 * règle (US-64) —, l'image déclarée par la page ensuite.
 *
 * Cette seconde est chargée **depuis le navigateur**, jamais recopiée par le
 * serveur avant qu'on garde (§1 « Ce que le serveur ne va pas chercher ») : la
 * requête part de l'appareil qui regarde, exactement comme la page est partie
 * du téléphone qui l'a partagée.
 *
 * `onError` la fait disparaître, et c'est indispensable plutôt que soigné : les
 * adresses d'images d'Instagram sont **signées et périment**. Une entrée qui
 * traîne trois semaines au sas verra la sienne expirer, et une image cassée
 * avec son icône de document déchiré vaut moins que pas d'image du tout.
 */
function Vignette({ entree }: { entree: ImportDraftItem }): React.JSX.Element | null {
  const [cassee, setCassee] = useState(false);
  const source = entree.photo_url ?? entree.prefill.image_url;
  if (source === null || cassee) return null;

  return (
    <img
      src={source}
      alt=""
      className="size-20 shrink-0 rounded-card object-cover"
      onError={() => { setCassee(true); }}
    />
  );
}

/**
 * Une puce de proposition (US-73), et la seule action qu'elle offre : la
 * **retirer**.
 *
 * Ton « à vérifier » — le même que celui des lignes d'ingrédient non reconnues
 * (US-26) — et non le ton des étiquettes acquises : ce qui est proposé et ce
 * qui est établi ne doivent pas se ressembler, sans quoi la proposition
 * devient une devinette silencieuse à laquelle on a seulement donné une
 * couleur (D8).
 *
 * Le « × » est décoratif (`aria-hidden`) : ce que lit une synthèse vocale est
 * le libellé du bouton, qui dit le geste **et** ce qu'il retire.
 */
function Puce({ libelle, onRetirer, disabled }: {
  libelle: string;
  onRetirer: () => void;
  disabled: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onRetirer}
      disabled={disabled}
      aria-label={`Retirer la proposition « ${libelle} »`}
      className={badgeClasses('warning', `gap-1 ${DISABLED_SHAPE}`)}
    >
      {libelle}
      <span aria-hidden="true">×</span>
    </button>
  );
}

/** Les propositions d'une entrée, sous la forme que la carte affiche. */
interface PuceProposee {
  libelle: string;
  /** Ce qui reste une fois cette puce retirée — l'état voulu, jamais un ordre. */
  restant: RangementPropose;
}

function pucesDe(propositions: RangementPropose): PuceProposee[] {
  const puces: PuceProposee[] = [];
  const libelleCourse = formatCourse(propositions.course);
  if (libelleCourse !== null) {
    puces.push({ libelle: libelleCourse, restant: { ...propositions, course: null } });
  }
  if (propositions.cuisine !== null) {
    puces.push({ libelle: propositions.cuisine, restant: { ...propositions, cuisine: null } });
  }
  for (const tag of propositions.tags) {
    puces.push({ libelle: tag, restant: { ...propositions, tags: propositions.tags.filter((autre) => autre !== tag) } });
  }
  return puces;
}

/**
 * « Rejoindra : *Plats italiens* · *Moins de 30 min* » — **la ligne qui a de la
 * valeur** (US-73).
 *
 * Elle ne dit pas une catégorie, elle dit ce qui va se passer. C'est ce qui
 * rend une proposition vérifiable sans rien connaître du modèle qui l'a faite :
 * on ne juge pas « italienne, est-ce juste ? » mais « ça part dans *Plats
 * italiens*, est-ce que je le veux ? ».
 *
 * Calculée **ici**, dans le navigateur, avec le prédicat partagé : retirer une
 * puce la met à jour dans le même rendu, sans aller-retour. Une ligne qui
 * clignoterait une demi-seconde après le geste ne se lirait plus comme une
 * conséquence.
 *
 * Les collections sont celles que `GET /api/collections` a rendues, donc
 * **celles du demandeur** : celles des autres diraient ce qu'ils rangent
 * (US-46, vecteur 6).
 */
function rejoindra(
  entree: ImportDraftItem,
  propositions: RangementPropose | null,
  collections: readonly CollectionWithQuery[],
): CollectionWithQuery[] {
  const candidat = {
    ...entree.candidat,
    course: entree.candidat.course ?? propositions?.course ?? null,
    cuisine: entree.candidat.cuisine ?? propositions?.cuisine ?? null,
    tags: [...entree.candidat.tags, ...(propositions?.tags ?? [])],
  };
  return collections.filter((collection) => matchesCollectionQuery(candidat, collection.query));
}

export interface ImportInboxProps {
  /**
   * « Ouvrir pour corriger » : l'écran de validation habituel, préremplí de
   * l'entrée — `origine` comprise, pour que le bandeau « lu par l'IA » d'US-70
   * ne disparaisse pas au moment où il sert.
   */
  onCorriger: (entree: ImportDraftItem) => void;
  /** Une entrée est entrée dans la bibliothèque : la liste et la pastille se relisent. */
  onGardee?: () => void;
}

export function ImportInbox({ onCorriger, onGardee }: ImportInboxProps): React.JSX.Element {
  const [entrees, setEntrees] = useState<ImportDraftItem[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Identifiants en cours de traitement : la carte concernée se désactive, les autres restent vives. */
  const [enCours, setEnCours] = useState<readonly string[]>([]);
  const [tout, setTout] = useState(false);
  /**
   * L'heure de la dernière lecture, relevée **au moment où la liste arrive** et
   * non pendant le rendu : `Date.now()` dans le corps d'un composant rend le
   * rendu impur, donc instable d'un re-rendu à l'autre. Elle ne sert qu'à dire
   * « encore n jours » ; la péremption elle-même est côté serveur.
   */
  const [luA, setLuA] = useState(0);
  /**
   * Les collections du membre (US-73), lues une fois : la ligne « Rejoindra »
   * les rejoue à chaque retrait de puce, dans le navigateur.
   *
   * Une lecture qui échoue laisse la liste vide, et l'écran perd seulement la
   * ligne — les propositions restent affichées et « Garder » reste offert. Le
   * sas n'a jamais dépendu des collections pour fonctionner.
   */
  const [collections, setCollections] = useState<readonly CollectionWithQuery[]>([]);

  const recharger = useCallback(async (): Promise<ImportDraftItem[]> => {
    const reponse = await listImportDrafts();
    setEntrees(reponse.drafts);
    setLuA(Date.now());
    return reponse.drafts;
  }, []);

  useEffect(() => {
    let vivant = true;
    listImportDrafts()
      .then((reponse) => {
        if (!vivant) return;
        setEntrees(reponse.drafts);
        setLuA(Date.now());
      })
      .catch((err: unknown) => {
        if (!vivant) return;
        setEntrees([]);
        setErreur(err instanceof Error ? err.message : 'Le sas n’a pas pu être lu.');
      });
    return () => { vivant = false; };
  }, []);

  useEffect(() => {
    let vivant = true;
    listCollections()
      .then((liste) => { if (vivant) setCollections(liste); })
      .catch(() => {
        // Silencieux, et c'est délibéré : l'échec ne coûte que la ligne
        // « Rejoindra ». Une bannière rouge pour une phrase d'agrément ferait
        // croire que la capture est en danger, ce qu'elle n'est pas.
      });
    return () => { vivant = false; };
  }, []);

  /**
   * Retirer une puce : **l'écran d'abord, le serveur ensuite**.
   *
   * L'état local part immédiatement — c'est ce qui met la ligne « Rejoindra » à
   * jour dans le même rendu. L'aller-retour ne fait que persister le geste,
   * pour que « Garder », plus tard, entre une recette **sans** la valeur
   * retirée. S'il échoue, la liste est relue : mieux vaut voir revenir une puce
   * qu'on croyait retirée que garder une recette rangée par une valeur qu'on
   * avait écartée.
   */
  async function retirer(entree: ImportDraftItem, restant: RangementPropose): Promise<void> {
    const vide = restant.course === null && restant.cuisine === null && restant.tags.length === 0;
    setEntrees((liste) => (liste ?? []).map((autre) => (
      autre.id === entree.id ? { ...autre, propositions: vide ? null : restant } : autre
    )));
    try {
      const relue = await majPropositions(entree.id, restant);
      setEntrees((liste) => (liste ?? []).map((autre) => (autre.id === relue.id ? relue : autre)));
    } catch (err: unknown) {
      setErreur(err instanceof Error ? err.message : 'La proposition n’a pas pu être retirée.');
      await recharger().catch(() => undefined);
    }
  }

  async function agir(id: string, action: () => Promise<unknown>): Promise<void> {
    setEnCours((liste) => [...liste, id]);
    setErreur(null);
    try {
      await action();
      await recharger();
      onGardee?.();
    } catch (err: unknown) {
      setErreur(err instanceof Error ? err.message : 'Action impossible.');
      // L'entrée reste : un échec ne jette rien et ne fait rien disparaître de
      // l'écran. C'est le pendant du 404 côté serveur — on ne perd jamais une
      // capture sur un aléa de réseau.
      await recharger().catch(() => undefined);
    } finally {
      setEnCours((liste) => liste.filter((autre) => autre !== id));
    }
  }

  /**
   * « Tout garder » — l'usage du dimanche soir.
   *
   * Séquentiel et non parallèle : la première entrée qui refuse (une entrée
   * sans titre, par exemple) arrête la série et laisse le reste en place, plutôt
   * que de disperser des échecs dans un lot dont on ne saurait plus ce qui est
   * passé. Le décompte est sur le bouton pour que le geste dise son ampleur.
   */
  async function toutGarder(): Promise<void> {
    setTout(true);
    setErreur(null);
    try {
      for (const entree of entrees ?? []) {
        if (entree.prefill.title.trim() === '') continue;
        await garderImportDraft(entree.id);
      }
    } catch (err: unknown) {
      setErreur(err instanceof Error ? err.message : 'Certaines entrées n’ont pas pu être gardées.');
    } finally {
      await recharger().catch(() => undefined);
      onGardee?.();
      setTout(false);
    }
  }

  if (entrees === null) return <Loading label="Lecture du sas" lines={2} />;

  return (
    <section aria-label="Le sas" className="flex flex-col gap-4">
      <PageHeader
        eyebrow="Mis de côté"
        title="Le sas"
        lede="Ce que vous avez capturé et pas encore gardé. Rien n’entre dans la bibliothèque sans un geste."
        back={<Link to={paths.library} className={buttonClasses('quiet', 'compact')}><Icon name="fleche-gauche" size={0.9} /> Bibliothèque</Link>}
        actions={entrees.length > 1 && (
          <Button variant="primary" onClick={() => { void toutGarder(); }} disabled={tout} aria-busy={tout}>
            Tout garder ({entrees.length})
          </Button>
        )}
      />

      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}

      {entrees.length === 0 && (
        <EmptyState
          icon={<Icon name="livre" size={2} />}
          title="Le sas est vide."
          description={
            <>
              C’est ici qu’atterrit ce que vous mettez de côté sans avoir le temps de le relire :
              la recette reste en attente, elle n’est ni dans la bibliothèque, ni dans la recherche,
              et elle s’efface d’elle-même au bout de trente jours si vous n’y touchez pas.
              Le chemin le plus court pour le remplir est le raccourci de partage, à installer une
              fois depuis votre téléphone.
            </>
          }
          action={
            /* Le lien manquait dans US-71 — le raccourci n'existait pas encore,
               et fabriquer un lien mort aurait été pire que son absence. Il est
               ici, comme prévu. */
            <Link to={paths.settings} className={buttonClasses('primary')}>
              Installer le raccourci
            </Link>
          }
        />
      )}

      <ul className="flex list-none flex-col gap-3 p-0">
        {entrees.map((entree) => {
          const occupe = enCours.includes(entree.id) || tout;
          const ingredients = lignes(entree.prefill.ingredients_text);
          const titre = entree.prefill.title.trim();
          const jours = joursRestants(entree.expires_at, luA);
          /**
           * Une capture qui n'a rien rapporté : ni titre, ni ingrédient, ni
           * étape. Le seul cas où la carte doit dire autre chose que
           * « corrigez » — il n'y a rien à corriger, il faut une autre source.
           */
          /** Là où l'entrée ira si on la garde telle quelle — recalculé à chaque rendu, donc à chaque retrait. */
          const destinations = rejoindra(entree, entree.propositions, collections);
          const muette = titre === ''
            && ingredients.length === 0
            && entree.prefill.steps_text.trim() === ''
            && entree.prefill.source_url !== null;
          return (
            <li key={entree.id}>
              <Card className="flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <Vignette entree={entree} />
                  <div className="flex min-w-0 flex-col gap-1">
                    <h2 className="text-heading-3">
                      {titre === '' ? 'Sans titre' : titre}
                    </h2>
                    {/* La provenance, et rien de plus : une carte qui devient un
                        tableau de bord ne se lit plus d'un coup d'œil (§6 du
                        plan de vague, « la carte devient un tableau de bord »). */}
                    <p className="text-xs text-foreground-secondary">
                      {entree.prefill.source_name ?? 'Provenance inconnue'}
                      {entree.prefill.author !== null && ` · ${entree.prefill.author}`}
                      {entree.prefill.origine === 'ia' && ' · lu par l’IA'}
                      {jours <= 7 && ` · encore ${String(Math.max(jours, 0))} j`}
                    </p>
                    {/* **Alerte, jamais blocage** (§3.4 étape 6). Calculée à la
                        lecture de la liste et non à la capture : figée, elle
                        mentirait trente jours plus tard. « Garder » reste
                        offert — deux recettes proches valent mieux qu'un geste
                        refusé. */}
                    {entree.prefill.duplicate_warning !== null && (
                      <p className="text-xs text-warning">
                        Déjà dans la bibliothèque : « {entree.prefill.duplicate_warning.recipe_title} »
                      </p>
                    )}
                    {/* Instagram n'a rien donné — compte privé, publication
                        effacée, page changée. **Le geste n'est jamais perdu** :
                        l'entrée existe, avec son lien, et le chemin qui marche
                        toujours est nommé (C5, la capture d'écran). */}
                    {muette && (
                      <p className="text-xs text-foreground-secondary">
                        La page n’a pas donné de texte.{' '}
                        <Link to={paths.importPhoto}>Une capture d’écran ?</Link>
                      </p>
                    )}
                  </div>
                </div>

                {/* **Le rangement proposé** (US-73), et sa conséquence.
                    Deux lignes, pas une de plus : la première dit ce qu'on
                    propose, la seconde ce que ça va faire — et c'est la
                    seconde qui a de la valeur. */}
                {entree.propositions !== null && (
                  <div className="flex flex-wrap items-center gap-2">
                    {pucesDe(entree.propositions).map((puce) => (
                      <Puce
                        key={puce.libelle}
                        libelle={puce.libelle}
                        disabled={occupe}
                        onRetirer={() => { void retirer(entree, puce.restant); }}
                      />
                    ))}
                  </div>
                )}

                {destinations.length > 0 && (
                  <p className="text-xs text-foreground-secondary">
                    Rejoindra : {destinations.map((collection, index) => (
                      <span key={collection.id}>
                        {index > 0 && ' · '}
                        <em>{collection.name}</em>
                      </span>
                    ))}
                  </p>
                )}

                {ingredients.length > 0 && (
                  <CollapsiblePanel
                    label={`Ingrédients (${String(ingredients.length)})`}
                    sectionLabel="Ingrédients de l’entrée"
                  >
                    <ul className="flex list-none flex-col gap-1 p-0">
                      {ingredients.map((ligne, index) => <li key={`${ligne}-${String(index)}`}>{ligne}</li>)}
                    </ul>
                  </CollapsiblePanel>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {/* Une entrée sans titre ne se garde pas d'une tape : la
                      correction devient alors l'action primaire, plutôt qu'un
                      bouton qui échouerait à l'usage (§1, « toute fonction se
                      dégrade proprement »). */}
                  {titre !== '' && (
                    <Button
                      variant="primary"
                      onClick={() => { void agir(entree.id, () => garderImportDraft(entree.id)); }}
                      disabled={occupe}
                      aria-busy={occupe}
                    >
                      Garder
                    </Button>
                  )}
                  <Button
                    variant={titre === '' ? 'primary' : 'secondary'}
                    onClick={() => { onCorriger(entree); }}
                    disabled={occupe}
                  >
                    Ouvrir pour corriger
                  </Button>
                  <Button
                    variant="quiet"
                    onClick={() => { void agir(entree.id, () => jeterImportDraft(entree.id)); }}
                    disabled={occupe}
                  >
                    Jeter
                  </Button>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
